/**
 * Tournament Utility Functions
 * 
 * A collection of helper functions for tournament operations:
 * - Creating tournament brackets
 * - Generating match pairings
 * - Handling tournament progression
 */
import { handleFirebaseError, formatFirebaseError, tournamentRateLimiter } from './errorHandling.js';

// Tournament-specific error types
export const TournamentErrors = {
  INVALID_FORMAT: 'tournament/invalid-format',
  INVALID_SIZE: 'tournament/invalid-size',
  INVALID_PARTICIPANTS: 'tournament/invalid-participants',
  MATCH_NOT_FOUND: 'tournament/match-not-found',
  WINNER_NOT_PARTICIPANT: 'tournament/winner-not-participant',
  INVALID_ROUND: 'tournament/invalid-round',
  INVALID_MATCH: 'tournament/invalid-match'
};

// Allowed tournament formats
const ALLOWED_FORMATS = ['single-elimination', 'double-elimination', 'round-robin'];

// Size limits for tournaments
const MIN_TOURNAMENT_SIZE = 2;
const MAX_TOURNAMENT_SIZE = 128;

/**
 * Validate tournament format and size
 * 
 * @param {string} format - Tournament format
 * @param {number} size - Tournament size
 * @throws {Error} If format or size is invalid
 */
const validateTournamentParams = (format, size) => {
  if (!ALLOWED_FORMATS.includes(format)) {
    const error = new Error(`Invalid tournament format: ${format}`);
    error.code = TournamentErrors.INVALID_FORMAT;
    throw error;
  }
  
  if (!Number.isInteger(size) || size < MIN_TOURNAMENT_SIZE || size > MAX_TOURNAMENT_SIZE) {
    const error = new Error(`Tournament size must be between ${MIN_TOURNAMENT_SIZE} and ${MAX_TOURNAMENT_SIZE}`);
    error.code = TournamentErrors.INVALID_SIZE;
    throw error;
  }
};

/**
 * Generate a tournament bracket structure based on the format and number of participants
 * 
 * @param {string} format - Tournament format (single-elimination, double-elimination, round-robin)
 * @param {number} size - The size of the bracket (number of participants/teams)
 * @return {Array} Generated brackets array
 * @throws {Error} If format or size is invalid
 */
export const generateBrackets = (format = 'single-elimination', size = 8) => {
  try {
    // Validate input parameters
    validateTournamentParams(format, size);
    
    // Create dummy participants based on size
    const dummyParticipants = Array(size).fill(null);
    
    switch (format) {
      case 'single-elimination':
        return generateSingleEliminationBracket(dummyParticipants);
      case 'double-elimination':
        return generateDoubleEliminationBracket(dummyParticipants);
      case 'round-robin':
        return generateRoundRobinBracket(dummyParticipants);
      default:
        return generateSingleEliminationBracket(dummyParticipants);
    }
  } catch (error) {
    return handleFirebaseError(error, 'generateBrackets');
  }
};

/**
 * Generate a single elimination tournament bracket
 * 
 * @param {Array} participants - Array of participant IDs
 * @return {Array} Generated brackets array
 */
const generateSingleEliminationBracket = (participants) => {
  if (!Array.isArray(participants)) {
    const error = new Error('Participants must be an array');
    error.code = TournamentErrors.INVALID_PARTICIPANTS;
    throw error;
  }
  
  // Ensure we have a power of 2 number of participants
  const filledParticipants = fillBracket(participants);
  const numParticipants = filledParticipants.length;
  const numRounds = Math.log2(numParticipants);
  const brackets = [];
  
  // First round - initial pairings
  const firstRoundMatches = [];
  for (let i = 0; i < numParticipants; i += 2) {
    firstRoundMatches.push({
      id: `round1-match${i/2 + 1}`,
      team1: filledParticipants[i],
      team2: filledParticipants[i + 1],
      winner: null,
      score: null,
      scheduledTime: null,
      completed: false,
      createdAt: new Date().toISOString()
    });
  }
  
  brackets.push({
    round: 1,
    matches: firstRoundMatches
  });
  
  // Generate subsequent rounds with empty matches
  for (let round = 2; round <= numRounds; round++) {
    const numMatches = numParticipants / Math.pow(2, round);
    const matches = [];
    
    for (let i = 0; i < numMatches; i++) {
      matches.push({
        id: `round${round}-match${i + 1}`,
        team1: null,
        team2: null,
        winner: null,
        score: null,
        scheduledTime: null,
        completed: false,
        createdAt: new Date().toISOString()
      });
    }
    
    brackets.push({
      round: round,
      matches: matches
    });
  }
  
  return brackets;
};

/**
 * Generate a double elimination tournament bracket
 * 
 * @param {Array} participants - Array of participant IDs
 * @return {Array} Generated brackets array
 */
const generateDoubleEliminationBracket = (participants) => {
  // Start with a single elimination bracket for the winners bracket
  const winnersBracket = generateSingleEliminationBracket(participants);
  
  // Add a losers bracket and final bracket
  // For simplicity, we'll just create empty brackets for losers rounds
  // In a real implementation, these would be more precisely structured
  const numParticipants = fillBracket(participants).length;
  const numRounds = Math.log2(numParticipants);
  
  // Losers bracket will have 2*log2(n)-1 rounds
  const losersBracket = [];
  
  for (let round = 1; round <= 2 * numRounds - 1; round++) {
    losersBracket.push({
      round: round + numRounds, // Start numbering after winners bracket
      bracket: 'losers',
      matches: [{
        id: `losers-round${round}-match1`,
        team1: null,
        team2: null,
        winner: null,
        score: null,
        scheduledTime: null
      }]
    });
  }
  
  // Finals (could be 1 or 2 matches depending on if losers bracket winner wins)
  const finals = {
    round: 2 * numRounds + 1,
    bracket: 'finals',
    matches: [{
      id: `finals`,
      team1: null,
      team2: null,
      winner: null,
      score: null,
      scheduledTime: null
    }]
  };
  
  return [...winnersBracket, ...losersBracket, finals];
};

/**
 * Generate a round robin tournament bracket
 * 
 * @param {Array} participants - Array of participant IDs
 * @return {Array} Generated brackets array
 */
const generateRoundRobinBracket = (participants) => {
  if (!participants || participants.length === 0) {
    return [];
  }
  
  // Ensure even number of participants by adding a dummy if necessary
  let teams = [...participants];
  if (teams.length % 2 !== 0) {
    teams.push('BYE'); // BYE represents a dummy team for odd number of participants
  }
  
  const numTeams = teams.length;
  const numRounds = numTeams - 1;
  const brackets = [];
  
  // Create rounds using circle method
  for (let round = 1; round <= numRounds; round++) {
    const matches = [];
    
    // First team is fixed, others rotate
    const fixedTeam = teams[0];
    
    // Create matches for this round
    for (let i = 0; i < numTeams / 2; i++) {
      const team1 = i === 0 ? fixedTeam : teams[i];
      const team2 = teams[numTeams - 1 - i];
      
      // Skip matches with BYE
      if (team1 !== 'BYE' && team2 !== 'BYE') {
        matches.push({
          id: `round${round}-match${i+1}`,
          team1,
          team2,
          winner: null,
          score: null,
          scheduledTime: null
        });
      }
    }
    
    brackets.push({
      round,
      matches
    });
    
    // Rotate teams for next round (keeping first team fixed)
    const teamsExceptFirst = teams.slice(1);
    teams = [
      teams[0],
      teams[numTeams - 1],
      ...teamsExceptFirst.slice(0, numTeams - 2)
    ];
  }
  
  return brackets;
};

/**
 * Fill a bracket to ensure we have a power of 2 number of participants
 * 
 * @param {Array} participants - Original participant array
 * @return {Array} Filled participant array
 */
const fillBracket = (participants) => {
  // Find next power of 2
  const numParticipants = participants.length;
  const nextPowerOfTwo = Math.pow(2, Math.ceil(Math.log2(numParticipants)));
  
  // Clone the original array
  const filledParticipants = [...participants];
  
  // Fill with nulls if needed
  while (filledParticipants.length < nextPowerOfTwo) {
    filledParticipants.push(null);
  }
  
  return filledParticipants;
};

/**
 * Advance a winner to the next round in a tournament bracket
 * 
 * @param {Array} brackets - Current brackets array
 * @param {number} roundIndex - Index of the current round
 * @param {number} matchIndex - Index of the match in the current round
 * @param {string} winnerId - ID of the winning team/participant
 * @param {string} score - Match score (e.g. "2-1")
 * @return {Array} Updated brackets array
 * @throws {Error} If any parameters are invalid
 */
export const advanceWinner = (brackets, roundIndex, matchIndex, winnerId, score) => {
  try {
    return tournamentRateLimiter.execute(() => _advanceWinner(brackets, roundIndex, matchIndex, winnerId, score));
  } catch (error) {
    return handleFirebaseError(error, 'advanceWinner');
  }
};

/**
 * Internal implementation of advanceWinner with validation
 */
const _advanceWinner = (brackets, roundIndex, matchIndex, winnerId, score) => {
  // Validate inputs
  if (!Array.isArray(brackets) || brackets.length === 0) {
    const error = new Error('Invalid brackets array');
    error.code = TournamentErrors.INVALID_FORMAT;
    throw error;
  }
  
  if (roundIndex < 0 || roundIndex >= brackets.length) {
    const error = new Error(`Invalid round index: ${roundIndex}`);
    error.code = TournamentErrors.INVALID_ROUND;
    throw error;
  }
  
  const currentRound = brackets[roundIndex];
  
  if (matchIndex < 0 || matchIndex >= currentRound.matches.length) {
    const error = new Error(`Invalid match index: ${matchIndex}`);
    error.code = TournamentErrors.INVALID_MATCH;
    throw error;
  }
  
  const currentMatch = currentRound.matches[matchIndex];
  
  // Verify the winner is one of the participants
  if (winnerId !== currentMatch.team1 && winnerId !== currentMatch.team2) {
    const error = new Error('Winner must be one of the match participants');
    error.code = TournamentErrors.WINNER_NOT_PARTICIPANT;
    throw error;
  }
  
  // Create a deep copy of the brackets array to avoid mutating the original
  const updatedBrackets = JSON.parse(JSON.stringify(brackets));
  
  // Update the winner and score for the specified match
  const match = updatedBrackets[roundIndex].matches[matchIndex];
  match.winner = winnerId;
  match.score = score;
  match.completed = true;
  match.updatedAt = new Date().toISOString();
  
  // Determine the match in the next round where this winner should advance
  if (roundIndex < updatedBrackets.length - 1) {
    const nextRound = updatedBrackets[roundIndex + 1];
    const nextMatchIndex = Math.floor(matchIndex / 2);
    
    if (nextMatchIndex < nextRound.matches.length) {
      const nextMatch = nextRound.matches[nextMatchIndex];
      
      // If matchIndex is even, this winner goes to team1 spot, otherwise team2
      if (matchIndex % 2 === 0) {
        nextMatch.team1 = winnerId;
      } else {
        nextMatch.team2 = winnerId;
      }
    }
  }
  
  return updatedBrackets;
};

/**
 * Get tournament standings from round-robin results
 * 
 * @param {Array} brackets - Tournament brackets array
 * @return {Array} Array of participants with wins, losses, etc.
 */
export const getStandings = (brackets) => {
  try {
    // Validate input
    if (!Array.isArray(brackets) || brackets.length === 0) {
      const error = new Error('Invalid brackets array');
      error.code = TournamentErrors.INVALID_FORMAT;
      throw error;
    }
    
    // Create a map to store participant stats
    const standingsMap = new Map();
    
    // Process each match in each round
    for (const round of brackets) {
      for (const match of round.matches) {
        if (!match.completed) continue;
        
        const team1 = match.team1;
        const team2 = match.team2;
        
        // Initialize stats for teams if they don't exist
        if (!standingsMap.has(team1)) {
          standingsMap.set(team1, { teamId: team1, wins: 0, losses: 0, draws: 0, points: 0 });
        }
        if (!standingsMap.has(team2)) {
          standingsMap.set(team2, { teamId: team2, wins: 0, losses: 0, draws: 0, points: 0 });
        }
        
        // Update stats based on match result
        if (match.winner) {
          if (match.winner === team1) {
            // Team 1 won
            standingsMap.get(team1).wins += 1;
            standingsMap.get(team1).points += 3;
            standingsMap.get(team2).losses += 1;
          } else {
            // Team 2 won
            standingsMap.get(team2).wins += 1;
            standingsMap.get(team2).points += 3;
            standingsMap.get(team1).losses += 1;
          }
        } else if (match.score) {
          // Assume a draw if score exists but no winner
          standingsMap.get(team1).draws += 1;
          standingsMap.get(team1).points += 1;
          standingsMap.get(team2).draws += 1;
          standingsMap.get(team2).points += 1;
        }
      }
    }
    
    // Convert map to array and sort by points (descending)
    return Array.from(standingsMap.values())
      .sort((a, b) => b.points - a.points);
      
  } catch (error) {
    return handleFirebaseError(error, 'getStandings');
  }
};

/**
 * Seed teams in a tournament bracket using a seeding algorithm
 * 
 * @param {Array} teams - Array of team objects with rankings or seeds
 * @param {string} method - Seeding method ('standard', 'snake', 'random')
 * @return {Array} Array of team IDs in seeded order
 * @throws {Error} If teams array or method is invalid
 */
export const seedTeams = (teams, method = 'standard') => {
  try {
    // Validate inputs
    if (!Array.isArray(teams)) {
      const error = new Error('Teams must be an array');
      error.code = TournamentErrors.INVALID_PARTICIPANTS;
      throw error;
    }
    
    const allowedMethods = ['standard', 'snake', 'random'];
    if (!allowedMethods.includes(method)) {
      const error = new Error(`Invalid seeding method: ${method}`);
      error.code = TournamentErrors.INVALID_FORMAT;
      throw error;
    }
    
    // Make a copy of the teams array to avoid mutating the original
    const teamsCopy = [...teams];
    
    switch (method) {
      case 'standard':
        return standardSeed(teamsCopy);
      case 'snake':
        return snakeSeed(teamsCopy);
      case 'random':
        return randomSeed(teamsCopy);
      default:
        return standardSeed(teamsCopy);
    }
  } catch (error) {
    return handleFirebaseError(error, 'seedTeams');
  }
};

/**
 * Apply standard tournament seeding (1 vs lowest seed, 2 vs second lowest, etc.)
 * 
 * @param {Array} teamIds - Array of team IDs sorted by seed
 * @return {Array} Rearranged array for tournament pairing
 */
const standardSeed = (teamIds) => {
  const n = teamIds.length;
  const nextPowerOfTwo = Math.pow(2, Math.ceil(Math.log2(n)));
  const result = new Array(nextPowerOfTwo).fill(null);
  
  // Standard seeding pattern for power of 2 brackets
  for (let i = 0; i < n; i++) {
    // This formula creates the standard tournament seeding pattern
    const position = getBracketPosition(i + 1, nextPowerOfTwo);
    result[position - 1] = teamIds[i];
  }
  
  return result;
};

/**
 * Get the position in a bracket for a given seed
 * 
 * @param {number} seed - Team seed (1 is highest)
 * @param {number} bracketSize - Size of the bracket (must be power of 2)
 * @return {number} Position in the bracket (1-based)
 */
const getBracketPosition = (seed, bracketSize) => {
  // Base case
  if (bracketSize === 1) return 1;
  
  const half = bracketSize / 2;
  if (seed <= half) {
    // Top half of bracket
    return 2 * getBracketPosition(seed, half) - 1;
  } else {
    // Bottom half of bracket
    return 2 * getBracketPosition(seed - half, half);
  }
};

/**
 * Apply snake seeding for group-based tournaments
 * 
 * @param {Array} teamIds - Array of team IDs sorted by seed
 * @return {Array} Rearranged array for tournament pairing
 */
const snakeSeed = (teamIds) => {
  // Determine the number of groups (default to 4)
  const numGroups = 4;
  const result = [];
  
  // Create empty groups
  const groups = Array(numGroups).fill().map(() => []);
  
  // Assign teams to groups in snake pattern
  // 1, 2, 3, 4, 8, 7, 6, 5, 9, 10, 11, 12, ...
  let forward = true;
  let groupIndex = 0;
  
  teamIds.forEach((teamId, index) => {
    if (teamId) {
      groups[groupIndex].push(teamId);
      
      if (forward) {
        groupIndex++;
        if (groupIndex >= numGroups) {
          groupIndex = numGroups - 1;
          forward = false;
        }
      } else {
        groupIndex--;
        if (groupIndex < 0) {
          groupIndex = 0;
          forward = true;
        }
      }
    }
  });
  
  // Flatten groups back to a single array
  groups.forEach(group => {
    result.push(...group);
    // Add nulls to ensure even number in each group if needed
    if (group.length % 2 !== 0) {
      result.push(null);
    }
  });
  
  return result;
};

/**
 * Apply random seeding
 * 
 * @param {Array} teamIds - Array of team IDs
 * @return {Array} Randomly shuffled array
 */
const randomSeed = (teamIds) => {
  // Create a copy of the array
  const shuffled = [...teamIds];
  
  // Fisher-Yates shuffle
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled;
};

/**
 * Validate a tournament bracket structure
 * 
 * @param {Array} brackets - Brackets to validate
 * @return {Object} Validation result with isValid and errors properties
 */
export const validateBrackets = (brackets) => {
  const errors = [];
  
  // Check if brackets is an array
  if (!Array.isArray(brackets)) {
    errors.push('Brackets must be an array');
    return { isValid: false, errors };
  }
  
  // Check if brackets is empty
  if (brackets.length === 0) {
    errors.push('Brackets array cannot be empty');
    return { isValid: false, errors };
  }
  
  // Check each round
  brackets.forEach((round, roundIndex) => {
    // Check if round has matches property
    if (!round.hasOwnProperty('matches') || !Array.isArray(round.matches)) {
      errors.push(`Round ${roundIndex + 1} must have a matches array`);
      return;
    }
    
    // Check each match
    round.matches.forEach((match, matchIndex) => {
      // Check if match has required properties
      if (!match.hasOwnProperty('id')) {
        errors.push(`Match ${matchIndex + 1} in round ${roundIndex + 1} must have an id`);
      }
      
      // For completed matches, verify winner is one of the participants
      if (match.completed && match.winner) {
        if (match.winner !== match.team1 && match.winner !== match.team2) {
          errors.push(`Match ${match.id}: Winner must be one of the participants`);
        }
      }
    });
  });
  
  return { 
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Calculate time remaining until tournament starts
 * 
 * @param {string|Date} startTime - Tournament start time
 * @return {Object} Object with days, hours, minutes remaining
 */
export const getTimeUntilStart = (startTime) => {
  const start = new Date(startTime);
  const now = new Date();
  
  // If startTime is in the past, return zeros
  if (start <= now) {
    return { days: 0, hours: 0, minutes: 0, isStarted: true };
  }
  
  // Calculate time difference in milliseconds
  const diff = start - now;
  
  // Convert to days, hours, minutes
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  return { days, hours, minutes, isStarted: false };
}; 